import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Download, CheckCircle2, MapPin, Calendar, GraduationCap } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import CountUp from "@/components/CountUp";

const PDF_URL =
  "https://customer-assets.emergentagent.com/job_8d6d79c0-9556-40a3-9c66-4524c7c848b8/artifacts/pziidep6_The%20University%20of%20Reading%20and%20Calmbreeze%20case%20study.pdf";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  "@id": "https://calmbreeze.co.uk/case-studies/university-of-reading#article",
  "headline": "University of Reading — 560 fire dampers, 77 buildings, BS 9999 compliance",
  "description":
    "How Calmbreeze delivered fire damper drop testing, HVAC surveying and location mapping across all University of Reading campuses — 560 dampers, 77 buildings, 1,792 hours over five months, term-time delivery.",
  "author": { "@id": "https://calmbreeze.co.uk/#org" },
  "publisher": { "@id": "https://calmbreeze.co.uk/#org" },
  "about": {
    "@type": "EducationalOrganization",
    "name": "University of Reading",
    "url": "https://www.reading.ac.uk/"
  },
  "keywords": [
    "Fire damper drop testing",
    "BS 9999 compliance",
    "University of Reading",
    "HVAC surveying",
    "Locatrix",
    "Term-time HVAC delivery"
  ],
  "url": "https://calmbreeze.co.uk/case-studies/university-of-reading"
};

const StatBig = ({ value, label, color = "text-brand", numeric, suffix = "" }) => (
  <div>
    <div className={`font-mono ${color} text-[2.5rem] md:text-[3rem] leading-none tracking-tightest font-medium`}>
      {typeof numeric === "number" ? (
        <CountUp to={numeric} duration={1800} suffix={suffix} data-testid={`case-reading-count-${numeric}`} />
      ) : (
        value
      )}
    </div>
    <div className="mt-3 text-[11px] tracking-overline uppercase text-muted font-mono">{label}</div>
  </div>
);

const CaseStudyReading = () => {
  return (
    <>
      <Seo
        title="University of Reading Case Study — Fire Damper Compliance | Calmbreeze"
        description="560 fire dampers · 77 buildings · 3 campuses · 1,792 hours. How Calmbreeze delivered BS 9999 fire damper compliance across the University of Reading during full term-time occupation."
        path="/case-studies/university-of-reading"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="pt-[140px] pb-16 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <Link
          to="/case-studies"
          data-testid="case-reading-back"
          className="inline-flex items-center gap-2 font-mono text-[11px] tracking-overline uppercase text-muted hover:text-brand transition-colors mb-8"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All case studies
        </Link>
        <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-6">
          [ Case Study · Higher Education ]
        </div>
        <h1 className="font-display font-bold text-[2.5rem] md:text-[4.5rem] leading-[0.98] tracking-tightest max-w-5xl">
          University of Reading —{" "}
          <span className="text-brand">560 fire dampers, term-time delivered.</span>
        </h1>
        <p className="mt-8 text-[18px] text-muted-foreground max-w-2xl leading-relaxed">
          A five-month, three-campus HVAC compliance programme delivered without a single day of
          academic disruption &mdash; giving the University a defensible, mapped baseline for
          BS 9999.
        </p>

        {/* Meta row */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-black/8">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-mono tracking-overline uppercase text-muted mb-2">
              <GraduationCap className="h-3.5 w-3.5" /> Sector
            </div>
            <div className="text-[14px] font-medium">Higher Education</div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[11px] font-mono tracking-overline uppercase text-muted mb-2">
              <MapPin className="h-3.5 w-3.5" /> Sites
            </div>
            <div className="text-[14px] font-medium">Whiteknights · London Road · Greenlands</div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[11px] font-mono tracking-overline uppercase text-muted mb-2">
              <Calendar className="h-3.5 w-3.5" /> Duration
            </div>
            <div className="text-[14px] font-medium">Five months · term-time</div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[11px] font-mono tracking-overline uppercase text-muted mb-2">
              <CheckCircle2 className="h-3.5 w-3.5" /> Standard
            </div>
            <div className="text-[14px] font-medium">BS 9999 · RRFSO 2005</div>
          </div>
        </div>
      </section>

      {/* HERO IMAGE — Official Whiteknights Campus Map */}
      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <figure className="rounded-md overflow-hidden border border-black/8 bg-paper-cool">
          <img
            src="/case-studies/whiteknights-campus-map.jpg"
            alt="Whiteknights Campus map — University of Reading estate surveyed by Calmbreeze"
            className="w-full h-auto"
            loading="lazy"
            decoding="async"
          />
          <figcaption className="px-5 py-3 text-[12px] font-mono tracking-wide text-muted-foreground bg-white border-t border-black/8">
            Whiteknights Campus &mdash; one of three University of Reading estates surveyed. Source: University of Reading.
          </figcaption>
        </figure>
      </section>

      {/* HEADLINE STATS */}
      <section className="py-20 bg-paper-cool mt-20">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
          <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-8">
            [ At a glance ]
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatBig numeric={560} label="Fire dampers surveyed &amp; tested" color="text-brand" />
            <StatBig numeric={77} label="Buildings surveyed" color="text-accent" />
            <StatBig numeric={1792} label="Engineer hours delivered" color="text-brand" />
            <StatBig value="23k" label="Students · zero disruption" color="text-ink" />
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="py-24 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Sticky sidebar */}
          <aside className="lg:col-span-3 lg:sticky lg:top-28 self-start">
            <div className="rounded-md border border-black/8 p-6 bg-white">
              <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-3">
                Download
              </div>
              <div className="font-display font-semibold text-[15px] mb-4 leading-snug">
                Full case study &mdash; PDF
              </div>
              <a
                href={PDF_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="case-reading-download-pdf"
                className="inline-flex items-center gap-2 w-full justify-center px-4 py-3 bg-brand text-white font-semibold text-[13px] rounded-sm hover:bg-accent transition-colors"
              >
                Download PDF <Download className="h-4 w-4" />
              </a>
              <div className="mt-4 text-[11px] text-muted-foreground leading-relaxed">
                Share with your compliance, FM or fire risk assessor team.
              </div>
            </div>

            <div className="mt-6 rounded-md border border-black/8 p-6 bg-paper-cream">
              <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-3">Scope</div>
              <ul className="space-y-2 text-[12.5px] text-ink/85">
                {[
                  "Fire damper drop testing",
                  "Survey & audit",
                  "Location mapping (with Locatrix)",
                  "Remedial works",
                  "Small works",
                  "Compliance reporting",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-brand mt-0.5 flex-shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Body copy */}
          <div className="lg:col-span-9 prose-editorial max-w-3xl">
            <Reveal>
              <h2 className="font-display font-bold text-[1.75rem] md:text-[2.25rem] tracking-tightest leading-tight mb-4">
                The brief
              </h2>
              <p className="text-[17px] text-muted-foreground leading-relaxed mb-6">
                The University of Reading remains compliant, safe and operational 24 hours a day,
                365 days a year &mdash; a need that intensifies in term-time, when all campuses run
                at full capacity for 23,000 students. The FM team needed a clear, accurate view of
                the university&rsquo;s HVAC infrastructure and defensible assurance of BS 9999
                compliance across every building on every campus.
              </p>
              <p className="text-[17px] text-muted-foreground leading-relaxed mb-6">
                Fire dampers are the frontline of protection against the breakout and spread of
                fire through ductwork in academic buildings. Assessing and documenting every
                damper across the estate &mdash; in term-time, without disrupting students or
                business functions &mdash; became the anchor of the engagement.
              </p>
            </Reveal>

            <Reveal>
              <h2 className="font-display font-bold text-[1.75rem] md:text-[2.25rem] tracking-tightest leading-tight mb-4 mt-14">
                Why Calmbreeze
              </h2>
              <p className="text-[17px] text-muted-foreground leading-relaxed mb-6">
                Calmbreeze was chosen for a reputation that extends beyond testing, mapping and
                documenting. The team&rsquo;s familiarity with complex, occupied estates &mdash;
                and a proactive, methodical approach to compliance surveying &mdash; matched the
                university&rsquo;s ambition to bring HVAC to the forefront of its FM agenda.
              </p>
            </Reveal>

            <Reveal>
              <h2 className="font-display font-bold text-[1.75rem] md:text-[2.25rem] tracking-tightest leading-tight mb-4 mt-14">
                Approach
              </h2>
              <p className="text-[17px] text-muted-foreground leading-relaxed mb-6">
                We ran the programme Agile-style: rolling surveys, immediate reporting, remediation
                and re-test cycles &mdash; all synced across a single source of truth.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
                {[
                  {
                    t: "Sitemap-first with Locatrix",
                    d: "All sites mapped with our partner Locatrix to capture known and unknown dampers &mdash; establishing a defensible HVAC baseline for years to come.",
                  },
                  {
                    t: "Daily pre/post-service reporting",
                    d: "Every audit and survey day closed with a report to the university&rsquo;s representatives, keeping the FM team ahead of findings.",
                  },
                  {
                    t: "Smartsheet dashboards",
                    d: "Real-time dashboards distributed the state of works, findings and remediation status across stakeholders on demand.",
                  },
                  {
                    t: "Access enablement",
                    d: "Mobile towers for high-level dampers, access panels for hidden dampers above plastered ceilings, and duct-door cutting where required.",
                  },
                ].map((card) => (
                  <div key={card.t} className="p-5 rounded-md border border-black/8 bg-paper-cool">
                    <div className="font-display font-semibold text-[1rem] tracking-tight mb-2">{card.t}</div>
                    <p
                      className="text-[13px] text-muted-foreground leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: card.d }}
                    />
                  </div>
                ))}
              </div>
              <p className="text-[17px] text-muted-foreground leading-relaxed mb-6">
                Findings were reported back, assessed and any faulty dampers repaired inside the
                same survey window &mdash; then re-tested to close the loop.
              </p>
            </Reveal>

            <Reveal>
              <h2 className="font-display font-bold text-[1.75rem] md:text-[2.25rem] tracking-tightest leading-tight mb-4 mt-14">
                Outcome
              </h2>
              <ul className="space-y-3 mb-8">
                {[
                  "An accurate, mapped view of the University's HVAC infrastructure and compliance standing",
                  "BS 9999 fire damper compliance assured across all three campuses",
                  "Day-to-day term-time services protected for current and future students",
                  "A baseline for the University's long-term HVAC strategy",
                ].map((o) => (
                  <li key={o} className="flex items-start gap-3 text-[15px] text-ink/90">
                    <CheckCircle2 className="h-4 w-4 text-brand mt-1 flex-shrink-0" />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
              <div className="p-6 rounded-md bg-ink text-white">
                <div className="font-mono text-[10px] tracking-overline uppercase text-white/60 mb-2">Delivered</div>
                <div className="font-display font-bold text-[1.25rem] md:text-[1.5rem] leading-snug">
                  77 buildings surveyed · 560 fire dampers tested · 1,792 hours of service · five-month term-time window.
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-paper-warm">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12 text-center">
          <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-4">
            [ Estate-wide compliance ]
          </div>
          <h3 className="font-display font-bold text-[2rem] md:text-[2.75rem] tracking-tightest leading-tight mb-6">
            Got an estate that needs the same treatment?
          </h3>
          <p className="text-[16px] text-muted-foreground max-w-xl mx-auto mb-8">
            Universities, hospitals, multi-site portfolios &mdash; we scope, survey and deliver in
            occupied buildings without disruption.
          </p>
          <Link
            to="/contact"
            data-testid="case-reading-cta-contact"
            className="inline-flex items-center gap-2 px-7 py-4 bg-brand text-white font-semibold text-[14px] rounded-sm hover:bg-accent transition-colors"
          >
            Start a conversation <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default CaseStudyReading;
